/**
 * Listening.
 *
 * Uses the browser's own recognition — no key, no audio leaves the device on
 * platforms that recognise locally. Interim results are surfaced as they arrive
 * so the golfer sees their words appear while speaking, which is the difference
 * between "it's listening" and "did that work?".
 */

interface RecognitionAlternative {
  transcript: string;
  confidence: number;
}
interface RecognitionResult {
  0: RecognitionAlternative;
  isFinal: boolean;
  length: number;
}
interface RecognitionEvent extends Event {
  resultIndex: number;
  results: { [i: number]: RecognitionResult; length: number };
}
interface RecognitionErrorEvent extends Event {
  error: string;
}
interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: RecognitionEvent) => void) | null;
  onerror: ((e: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

type Ctor = new () => SpeechRecognitionLike;

function ctor(): Ctor | null {
  const w = window as unknown as { SpeechRecognition?: Ctor; webkitSpeechRecognition?: Ctor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export const speechRecognitionSupported = () =>
  typeof window !== 'undefined' && ctor() != null;

export interface ListenHandlers {
  /** Fires repeatedly while speaking — drives the live transcript. */
  onInterim: (text: string) => void;
  /** Fires once, with the final transcript. */
  onFinal: (text: string) => void;
  onError: (message: string) => void;
  onEnd: () => void;
}

export interface Listener {
  stop: () => void;
}

export function listen(handlers: ListenHandlers): Listener | null {
  const C = ctor();
  if (!C) {
    handlers.onError('This browser cannot listen. Try Chrome or Safari.');
    return null;
  }

  const rec = new C();
  rec.lang = 'en-AU';
  // Single utterance: a golfer says one thing, not a monologue. Continuous
  // listening on a windy course is a battery drain and a privacy problem.
  rec.continuous = false;
  rec.interimResults = true;
  rec.maxAlternatives = 1;

  let settled = false;

  rec.onresult = (e) => {
    let interim = '';
    let final = '';
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i];
      if (r.isFinal) final += r[0].transcript;
      else interim += r[0].transcript;
    }
    if (interim) handlers.onInterim(interim.trim());
    if (final) {
      settled = true;
      handlers.onFinal(final.trim());
    }
  };

  rec.onerror = (e) => {
    const map: Record<string, string> = {
      'not-allowed': 'Microphone permission is off.',
      'service-not-allowed': 'Microphone permission is off.',
      'no-speech': "Didn't catch that.",
      network: 'Speech recognition needs a connection.',
      aborted: '',
    };
    const msg = map[e.error] ?? `Could not listen (${e.error}).`;
    if (msg) handlers.onError(msg);
  };

  rec.onend = () => {
    if (!settled) handlers.onEnd();
    else handlers.onEnd();
  };

  try {
    rec.start();
  } catch {
    handlers.onError('Already listening.');
    return null;
  }

  return { stop: () => rec.abort() };
}
